import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AuthProvider } from './contexts/AuthContext';

// Admin Components
import AdminLayout from './components/layouts/AdminLayout';
import Dashboard from './pages/admin/Dashboard';
import QuestionManager from './pages/admin/QuestionManager';
import CategoryManager from './pages/admin/CategoryManager';
import QuestionnaireManager from './pages/admin/QuestionnaireManager';
import ResponsesManager from './pages/admin/ResponsesManager';
import QuestionnairePreview from './pages/admin/QuestionnairePreview';
import UserManager from './pages/admin/users/UserManager';

// User Components
import UserLayout from './components/layouts/UserLayout';
import QuestionnaireForm from './pages/user/QuestionnaireForm';
import SubmissionSuccess from './pages/user/SubmissionSuccess';

// Auth Components
import Login from './pages/auth/Login';
import AuthDebug from './pages/auth/AuthDebug';
import UserDebug from './pages/auth/UserDebug';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RoleBasedRoute from './components/auth/RoleBasedRoute';
import Unauthorized from './pages/Unauthorized';
import Home from './pages/Home';

// Audit Management System Components
import AuditRoutes from './components/audit/AuditRoutes';
import AuditLayout from './components/layouts/AuditLayout';
import ClientLayout from './components/layouts/ClientLayout';
import ClientQuestionnaire from './pages/audit/client/ClientQuestionnaire';
import ThankYouPage from './pages/audit/client/ThankYouPage';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <CssBaseline />
          <Routes>
          {/* Root Route - Redirects based on authentication status */}
          <Route path="/" element={<Home />} />
          
          {/* Redirect /my-cases to /audit/my-cases */}
          <Route path="/my-cases" element={<Navigate to="/audit/my-cases" replace />} />
          
          {/* Auth Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/auth-debug" element={<AuthDebug />} />
          <Route path="/user-debug" element={<UserDebug />} />
          
          {/* Unauthorized Route */}
          <Route path="/unauthorized" element={<Unauthorized />} />
          
          {/* Admin Routes - Protected and restricted to administrators */}
          <Route path="/admin" element={<ProtectedRoute allowedRoles={['administrator']}><AdminLayout /></ProtectedRoute>}>
            <Route index element={<Dashboard />} />
            <Route path="questions" element={<RoleBasedRoute allowedRoles={['administrator', 'manager']}><QuestionManager /></RoleBasedRoute>} />
            <Route path="categories" element={<RoleBasedRoute allowedRoles={['administrator', 'manager']}><CategoryManager /></RoleBasedRoute>} />
            <Route path="questionnaires" element={<RoleBasedRoute allowedRoles={['administrator', 'manager']}><QuestionnaireManager /></RoleBasedRoute>} />
            <Route path="responses" element={<ResponsesManager />} />
            <Route path="preview/:id" element={<QuestionnairePreview />} />
            <Route path="users" element={<RoleBasedRoute allowedRoles={['administrator']}><UserManager /></RoleBasedRoute>} />
          </Route>
          
          {/* User Routes */}
          <Route path="/user" element={<UserLayout />}>
            <Route path="questionnaire/:token" element={<QuestionnaireForm />} />
            <Route path="success" element={<SubmissionSuccess />} />
          </Route>
          
          {/* Client Routes - Public access with restricted layout */}
          <Route path="/audit/questionnaire/:token" element={<ClientLayout />}>
            <Route index element={<ClientQuestionnaire />} />
          </Route>
          
          <Route path="/audit/thank-you/:token" element={<ClientLayout />}>
            <Route index element={<ThankYouPage />} />
          </Route>
          
          {/* Audit Management System Routes - Protected */}
          <Route path="/audit" element={<ProtectedRoute><AuditLayout /></ProtectedRoute>}>
            <Route path="*" element={<AuditRoutes />} />
          </Route>
          </Routes>
        </LocalizationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
